import { Formik, Form } from 'formik';
import * as yup from 'yup';
import { Box, Container, Typography, Paper, Link, IconButton, InputAdornment } from '@mui/material';
import { useSnackbar } from 'notistack';
import { FormikTextField } from '../components/FormikTextField';
import { FormikSubmitButton } from '../components/FormikSubmitButton';
import { useResetPasswordMutation } from '../services/authApi';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { useState } from 'react';
import { Visibility, VisibilityOff } from '@mui/icons-material';

const validationSchema = yup.object({
    newPassword: yup
        .string()
        .min(8, 'Password should be of minimum 8 characters length')
        .required('New Password is required'),
    confirmPassword: yup
        .string()
        .oneOf([yup.ref('newPassword'), null as any], 'Passwords must match')
        .required('Confirm Password is required'),
});

const ResetPassword = () => {
    const { enqueueSnackbar } = useSnackbar();
    const [resetPassword] = useResetPasswordMutation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    if (!token) {
        return (
            <Container component="main" maxWidth="sm">
                <Box sx={{ mt: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Typography variant="h6" color="error">
                        Invalid or missing reset token.
                    </Typography>
                    <Link component={RouterLink} to="/login" sx={{ mt: 2 }}>
                        Return to Login
                    </Link>
                </Box>
            </Container>
        );
    }

    return (
        <Container component="main" maxWidth="sm">
            <Box
                sx={{
                    marginTop: 8,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                <Paper
                    elevation={3}
                    sx={{
                        padding: 4,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        width: '100%',
                    }}
                >
                    <Typography component="h1" variant="h5" gutterBottom>
                        Reset Password
                    </Typography>
                    <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
                        Enter your new password below.
                    </Typography>

                    <Formik
                        initialValues={{
                            newPassword: '',
                            confirmPassword: '',
                        }}
                        validationSchema={validationSchema}
                        onSubmit={async (values, { setSubmitting }) => {
                            try {
                                await resetPassword({ token, newPassword: values.newPassword }).unwrap();
                                enqueueSnackbar('Password reset successfully. Please login with your new password.', { variant: 'success' });
                                navigate('/login');
                            } catch (error: any) {
                                const errorMessage = error?.data?.message || 'Failed to reset password';
                                enqueueSnackbar(errorMessage, { variant: 'error' });
                            } finally {
                                setSubmitting(false);
                            }
                        }}
                    >
                        <Form style={{ width: '100%' }}>
                            <FormikTextField
                                name="newPassword"
                                margin="normal"
                                label="New Password"
                                type={showPassword ? 'text' : 'password'}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={() => setShowPassword(!showPassword)}
                                                edge="end"
                                            >
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <FormikTextField
                                name="confirmPassword"
                                margin="normal"
                                label="Confirm New Password"
                                type={showConfirmPassword ? 'text' : 'password'}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                edge="end"
                                            >
                                                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <FormikSubmitButton
                                fullWidth
                                variant="contained"
                                sx={{ mt: 3, mb: 2 }}
                                loadingText="Resetting..."
                            >
                                Reset Password
                            </FormikSubmitButton>
                        </Form>
                    </Formik>
                </Paper>
            </Box>
        </Container>
    );
};

export default ResetPassword;
